import Papa from 'papaparse';

/**
 * Parse CSV text to array of objects
 */
export function parseCSV(csvText) {
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim()
  });

  if (result.errors.length > 0) {
    console.error('CSV parsing errors:', result.errors);
  }

  return result.data.map(row => {
    const cleaned = {};
    for (const [key, value] of Object.entries(row)) {
      cleaned[key] = typeof value === 'string' ? value.trim() : value;
    }

    // Reconstruct ROI data structure from flat CSV columns
    if (cleaned.automation_area || cleaned.automation_potential ||
        cleaned.probability_conservative || cleaned.probability_likely || cleaned.probability_optimistic ||
        cleaned.number_of_interactions || cleaned.avg_time_per_interaction) {
      cleaned.roi_cases = {
        Conservative: {
          automation_area: cleaned.automation_area,
          automation_potential: cleaned.automation_potential,
          probability: cleaned.probability_conservative,
          number_of_interactions: cleaned.number_of_interactions,
          avg_time_per_interaction: cleaned.avg_time_per_interaction
        },
        Likely: {
          automation_area: cleaned.automation_area,
          automation_potential: cleaned.automation_potential,
          probability: cleaned.probability_likely,
          number_of_interactions: cleaned.number_of_interactions,
          avg_time_per_interaction: cleaned.avg_time_per_interaction
        },
        Optimistic: {
          automation_area: cleaned.automation_area,
          automation_potential: cleaned.automation_potential,
          probability: cleaned.probability_optimistic,
          number_of_interactions: cleaned.number_of_interactions,
          avg_time_per_interaction: cleaned.avg_time_per_interaction
        }
      };

      // Remove flat ROI fields from cleaned object
      delete cleaned.automation_area;
      delete cleaned.automation_potential;
      delete cleaned.probability_conservative;
      delete cleaned.probability_likely;
      delete cleaned.probability_optimistic;
      delete cleaned.number_of_interactions;
      delete cleaned.avg_time_per_interaction;
    }

    return cleaned;
  });
}

/**
 * Convert array of objects to CSV text
 */
export function convertToCSV(data, columns = null) {
  if (!data || data.length === 0) {
    return '';
  }

  // Default ROI values
  const defaultROI = {
    automation_area: 'Self-service',
    automation_potential: '80%',
    probability_conservative: '65%',
    probability_likely: '80%',
    probability_optimistic: '95%',
    number_of_interactions: '10000',
    avg_time_per_interaction: '30 sec'
  };

  // Flatten ROI data structure for CSV export
  const flattenedData = data.map(process => {
    const flat = { ...process };

    // Extract ROI data if it exists, otherwise use defaults
    if (process.roi_cases) {
      flat.automation_area = process.roi_cases.Conservative?.automation_area ||
                             process.roi_cases.Likely?.automation_area ||
                             process.roi_cases.Optimistic?.automation_area ||
                             defaultROI.automation_area;
      flat.automation_potential = process.roi_cases.Conservative?.automation_potential ||
                                  process.roi_cases.Likely?.automation_potential ||
                                  process.roi_cases.Optimistic?.automation_potential ||
                                  defaultROI.automation_potential;
      flat.probability_conservative = process.roi_cases.Conservative?.probability || defaultROI.probability_conservative;
      flat.probability_likely = process.roi_cases.Likely?.probability || defaultROI.probability_likely;
      flat.probability_optimistic = process.roi_cases.Optimistic?.probability || defaultROI.probability_optimistic;
      flat.number_of_interactions = process.roi_cases.Conservative?.number_of_interactions ||
                                    process.roi_cases.Likely?.number_of_interactions ||
                                    process.roi_cases.Optimistic?.number_of_interactions ||
                                    defaultROI.number_of_interactions;
      flat.avg_time_per_interaction = process.roi_cases.Conservative?.avg_time_per_interaction ||
                                      process.roi_cases.Likely?.avg_time_per_interaction ||
                                      process.roi_cases.Optimistic?.avg_time_per_interaction ||
                                      defaultROI.avg_time_per_interaction;

      // Remove nested ROI object from flat data
      delete flat.roi_cases;
    } else {
      // No ROI data exists, use all defaults
      flat.automation_area = defaultROI.automation_area;
      flat.automation_potential = defaultROI.automation_potential;
      flat.probability_conservative = defaultROI.probability_conservative;
      flat.probability_likely = defaultROI.probability_likely;
      flat.probability_optimistic = defaultROI.probability_optimistic;
      flat.number_of_interactions = defaultROI.number_of_interactions;
      flat.avg_time_per_interaction = defaultROI.avg_time_per_interaction;
    }

    return flat;
  });

  // If columns not specified, use all keys from first object
  const headers = columns || Object.keys(flattenedData[0]);

  const csv = Papa.unparse(flattenedData, {
    columns: headers,
    quotes: true,
    quoteChar: '"',
    escapeChar: '"',
    delimiter: ',',
    header: true,
    newline: '\n'
  });

  return csv;
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Get CSV column structure for different file types
 */
export function getCSVColumns(type) {
  switch (type) {
    case 'start':
      return ['id', 'name', 'summary'];

    case 'ai_suggested':
      return [
        'id',
        'name',
        'summary',
        'impact_productivity',
        'impact_customer',
        'impact_agent',
        'impact_compliance',
        'importance_productivity',
        'importance_customer',
        'importance_agent',
        'importance_compliance',
        'complexity'
      ];

    case 'final':
      return [
        'id',
        'name',
        'summary',
        'complexity',
        'complexity_score',
        'category',
        'impact_productivity',
        'impact_customer',
        'impact_agent',
        'impact_compliance',
        'importance_productivity',
        'importance_customer',
        'importance_agent',
        'importance_compliance',
        'weighted_score',
        'weighted_impact',
        'automation_area',
        'automation_potential',
        'probability_conservative',
        'probability_likely',
        'probability_optimistic',
        'number_of_interactions',
        'avg_time_per_interaction'
      ];

    default:
      return null;
  }
}
